import clsx from 'clsx';
import type { ReactNode } from 'react';
import type { PublishedStatus } from '../lib/api';

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={clsx('inline-block animate-spin rounded-full border-2 border-current border-t-transparent', className ?? 'h-4 w-4')}
      role="status"
      aria-label="Loading"
    />
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      <p className="text-sm font-medium text-slate-300">{title}</p>
      {hint ? <p className="max-w-md text-sm text-slate-500">{hint}</p> : null}
      {action}
    </div>
  );
}

export function ErrorBanner({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : String(error);
  const validation =
    error && typeof error === 'object' && 'validation' in error
      ? ((error as { validation: Array<{ field: string; title: string }> }).validation ?? [])
      : [];

  return (
    <div className="rounded-lg border border-red-900/60 bg-red-950/40 p-4 text-sm">
      <p className="font-medium text-red-200">{message}</p>
      {validation.length > 0 ? (
        <ul className="mt-2 space-y-1 text-red-300/90">
          {validation.map((issue) => (
            <li key={`${issue.field}-${issue.title}`}>
              <code className="rounded bg-red-900/40 px-1">{issue.field}</code> — {issue.title}
            </li>
          ))}
        </ul>
      ) : null}
      {onRetry ? (
        <button type="button" className="btn-ghost mt-3" onClick={onRetry}>
          Retry
        </button>
      ) : null}
    </div>
  );
}

const STATUS_STYLES: Record<PublishedStatus, string> = {
  active: 'bg-emerald-950/60 text-emerald-300 border border-emerald-900/60',
  pending: 'bg-amber-950/50 text-amber-300 border border-amber-900/60',
  limited: 'bg-orange-950/50 text-orange-300 border border-orange-900/60',
  archived: 'bg-slate-800 text-slate-400 border border-slate-700',
  deleted: 'bg-slate-800/60 text-slate-500 border border-slate-700',
  failed: 'bg-red-950/60 text-red-300 border border-red-900/60',
};

export function StatusBadge({ status, olxStatus }: { status: PublishedStatus; olxStatus?: string | null }) {
  return (
    <span className={clsx('badge', STATUS_STYLES[status])} title={olxStatus ? `OLX: ${olxStatus}` : undefined}>
      {status}
    </span>
  );
}

/**
 * Compact limit readout: "3 / 5 · 60d". Colour encodes headroom so an operator
 * can scan a dropdown without reading numbers.
 */
export function LimitPill({
  used,
  quota,
  windowDays,
  unlimited,
}: {
  used: number;
  quota: number | null;
  windowDays?: number | null;
  unlimited: boolean;
}) {
  if (unlimited || quota === null) {
    return <span className="badge border border-slate-700 bg-slate-800 text-slate-400">no limit</span>;
  }

  const remaining = Math.max(quota - used, 0);
  const tone =
    remaining === 0
      ? 'bg-red-950/60 text-red-300 border border-red-900/60'
      : remaining <= 1
        ? 'bg-amber-950/50 text-amber-300 border border-amber-900/60'
        : 'bg-emerald-950/50 text-emerald-300 border border-emerald-900/60';

  return (
    <span className={clsx('badge', tone)}>
      {used} / {quota}
      {windowDays ? <span className="opacity-70">· {windowDays}d</span> : null}
    </span>
  );
}

export function Toast({ tone, children }: { tone: 'success' | 'warning' | 'error'; children: ReactNode }) {
  const styles = {
    success: 'border-emerald-900/60 bg-emerald-950/40 text-emerald-200',
    warning: 'border-amber-900/60 bg-amber-950/40 text-amber-200',
    error: 'border-red-900/60 bg-red-950/40 text-red-200',
  }[tone];

  return <div className={clsx('rounded-lg border p-4 text-sm', styles)}>{children}</div>;
}
